import styled, { css } from 'styled-components';

import { lighten } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { DefaultStyledProps } from '@savgroup-front-common/types';

import {
  getBordersBasedOnProps,
  getInteractionsBasedOnProps,
  getTextColorBasedOnProps,
} from '../../theme/helpers';
import { fontStyle } from '../../theme/helpers/fontStyle';

import { colorBasedOnProps, colorHoverBasedOnProps } from './Checkbox.helpers';

interface CheckboxProps extends DefaultStyledProps {
  danger: boolean;
  isIndeterminate?: boolean;
  disabled: boolean;
  $hasDescription?: boolean;
  $withoutMargin?: boolean;
  $isHidden?: boolean;
  $withoutPaddingLeft?: boolean;
}

export const $StyledCheckbox = styled.span<
  CheckboxProps & { $isFlexAlign?: boolean }
>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
  position: absolute;
  top: ${({ $hasDescription }) => ($hasDescription ? spacing(2.5) : 0)};
  left: 0;
  width: ${spacing(5)};
  height: ${spacing(5)};
  border-radius: ${(props) => getBordersBasedOnProps(props).radius};
  border-width: 1px;
  border-color: ${colorBasedOnProps};
  border-style: solid;
  transform-origin: 50% 50%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition-property: background-color, border;
  transition-duration: ${(props) =>
    getInteractionsBasedOnProps(props).basicTiming};
  padding: 0;

  ${({ $isFlexAlign }) =>
    $isFlexAlign
      ? css`
          position: relative;
          margin-right: ${spacing('lg')};
          top: unset;
        `
      : null}
`;
export const $CheckContainer = styled.span`
  visibility: hidden;
  transition-duration: ${(props) =>
    getInteractionsBasedOnProps(props).basicTiming};

  & svg {
    color: ${({ theme }) => theme.systemColors.defaultTextColor};
  }
`;
export const $CheckboxInput = styled.input<CheckboxProps>`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: ${spacing(5)};
  height: ${spacing(5)};
  opacity: 0;
  cursor: pointer;

  display: ${({ $isHidden }) => ($isHidden ? 'none' : undefined)};

  &:hover,
  &:focus {
    + ${$StyledCheckbox} {
      border-color: ${(props) => lighten(0.07, colorHoverBasedOnProps(props))};
    }
  }

  &:disabled {
    cursor: not-allowed;
    + ${$StyledCheckbox} {
      background-color: ${({ theme }) => theme.colors.disabled};
      border-color: ${({ theme }) => theme.colors.disabled};
    }
  }

  &:active {
    + ${$StyledCheckbox} {
      transform: scale(
        ${(props) => getInteractionsBasedOnProps(props).activeScale}
      );
    }
  }

  &:checked {
    + ${$StyledCheckbox} {
      border-color: ${({ theme }) => theme.colors.default};
    }
    + ${$StyledCheckbox} ${$CheckContainer} {
      visibility: visible;

      transform: scale(1);
    }
  }
  & {
    + ${$StyledCheckbox} {
      background-color: ${({ isIndeterminate, ...rest }) =>
        isIndeterminate ? colorHoverBasedOnProps(rest) : null};
      border-color: ${({ isIndeterminate, ...rest }) =>
        isIndeterminate ? colorHoverBasedOnProps(rest) : null};
    }

    + ${$StyledCheckbox} ${$CheckContainer} {
      visibility: ${({ isIndeterminate }) =>
        isIndeterminate ? 'visible' : null};

      transform: ${({ isIndeterminate }) =>
        isIndeterminate ? 'scale(1)' : null};
    }
  }
`;

const container = css<CheckboxProps>`
  min-height: ${spacing(5)};
  position: relative;
  color: ${getTextColorBasedOnProps};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

interface CheckboxLabelProps extends CheckboxProps {
  $checked?: boolean;
  $shouldHighlightWhenChecked: boolean;
  $isRequired?: boolean;
}

export const $CheckboxLabel = styled.label<
  CheckboxLabelProps & { $isFlexAlign?: boolean }
>`
  ${container};
  display: block;
  padding-left: ${({ $isFlexAlign }) => ($isFlexAlign ? null : spacing('xxl'))};
  font-weight: ${({ $checked, $shouldHighlightWhenChecked }) =>
    $shouldHighlightWhenChecked && $checked ? fontWeight('semibold') : null};

  ${({ $isFlexAlign }) =>
    $isFlexAlign
      ? css`
          display: flex;
          align-items: center;
          position: relative;
        `
      : null}
`;

export const $CheckboxContainer = styled.div<CheckboxProps>`
  ${container};

  height: ${spacing(5)};
  width: ${spacing(5)};

  padding-left: ${({ $withoutPaddingLeft }) =>
    $withoutPaddingLeft ? null : spacing(5)};
  margin: ${({ $withoutMargin }) =>
    $withoutMargin ? null : `${spacing('md')} 0`};

  &&:hover * {
    border-color: ${({ theme, disabled }) =>
      !disabled ? theme.colors.primary : undefined};
  }
`;

export const $CheckboxDescription = styled.div<{ $isChecked?: boolean }>`
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  ${fontStyle('xs')};
`;

export const $LabelRequired = styled.span<{ $isRequired?: boolean }>`
  ${({ $isRequired }) => {
    if (!$isRequired) {
      return css``;
    }

    return css`
      &::after {
        margin-left: ${spacing('xs')};
        content: '*';
        color: ${({ theme }) => theme.colors.danger};
      }
    `;
  }}
`;
